/*
 * Copyright (C) 2005 - 2023. Cloud Software Group, Inc. All Rights Reserved. Confidential & Proprietary.
 * Licensed pursuant to commercial Cloud Software Group, Inc. End User License Agreement.
 */

import _ from 'underscore';

import {
    IDateTimeLabelFormats,
    dateTimeLabelFormatsMap,
    dateTimeTooltipFormatsMap
} from './dateTimeFormats';

export default {

    isTooltipFollowPointerEnabled(chartType :string) :boolean {
        return !_.contains([
            'line',
            'spline',
            'area',
            'stacked_area',
            'percent_area',
            'spline_area',
            'line_time_series',
            'spline_time_series',
            'area_time_series',
            'spline_area_time_series',
            'pie',
            'semi_pie',
            'dual_level_pie',
            'heat_map',
            'heat_map_time_series',
            'dual_measure_tree_map',
            'tree_map',
            'one_parent_tree_map'
        ], chartType);
    },

    isDualAxisChart(chartType :string) :boolean {
        return _.contains([
            'column_line',
            'column_spline',
            'stacked_column_line',
            'stacked_column_spline'
        ], chartType);
    },

    isMultiAxisChart(chartType :string) :boolean {
        return _.contains([
            'multi_axis_line',
            'multi_axis_spline',
            'multi_axis_column'
        ], chartType);
    },

    isDualOrMultiAxisChart(chartType :string) :boolean {
        return this.isDualAxisChart(chartType) || this.isMultiAxisChart(chartType);
    },

    isHeatMapBasedChart(chartType :string) :boolean {
        return _.contains([
            'heat_map',
            'heat_map_time_series'
        ], chartType);
    },

    doesChartUseShadow(chartType :string) :boolean {
        return !_.contains([
            'dual_level_pie',
            'heat_map',
            'heat_map_time_series',
            'bubble',
            'dual_measure_tree_map',
            'tree_map',
            'one_parent_tree_map'
        ], chartType);
    },

    getDateTimeLabelFormats(categorizerName :string) :IDateTimeLabelFormats {
        return dateTimeLabelFormatsMap[categorizerName];
    },

    getDateTimeTooltipFormats(categorizerName :string) :string {
        return dateTimeTooltipFormatsMap[categorizerName];
    },

    escapeTitle(title :string) :string {

        // because of HC library's strange escaping we have to manually escape that they
        // are de-escaping. See function unescapeAngleBrackets() in HC
        return title.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    },

    // eslint-disable-next-line class-methods-use-this
    correctEmptySeriesNames(highchartsOptions :any) :any {

        // This is the fix for issue found in ticket
        // https://jira.tibco.com/browse/JS-33467
        // see comment about "Series 3" issue

        // So, basically, for some cases we get empty strings from server as a column names
        // and if we pass these empty column names to HC it would convert it to "Series X" format
        // We don't need it and that's why we convert it to one space.

        // Since the column names goes to Series configuration of HC we need to check each series
        // against empty .name key

        _.each(highchartsOptions.series, (series :any) => {
            if (series.name === '') {
                // eslint-disable-next-line no-param-reassign
                series.name = ' ';
            }
        });

        return highchartsOptions;
    },

    propagatePlotOptionsWithShadow(highchartsOptions :any, chartType :string) :any {

        const useShadow = this.doesChartUseShadow(chartType);

        const { plotOptions } = highchartsOptions;
        let hcChartTypes = [highchartsOptions.chart.type];

        if (hcChartTypes.length === 0) {
            hcChartTypes = _.pluck(highchartsOptions.series, 'type');
        }

        _.each(hcChartTypes, (type :any) => {
            if (!plotOptions[type]) {
                plotOptions[type] = {};
            }
            plotOptions[type].shadow = useShadow;
        });

        return highchartsOptions;
    },

    setTurboThreshold(highchartsOptions :any) :any {

        const { plotOptions } = highchartsOptions;

        let maxSeriesLength = -Infinity;
        _.each(highchartsOptions.series, (serie :any) => {
            maxSeriesLength = Math.max(serie.data.length, maxSeriesLength);
        });

        if (maxSeriesLength > 1000) {
            (plotOptions.series || plotOptions.pie || plotOptions.treemap).turboThreshold = maxSeriesLength + 1;
        }

        return {
            ...highchartsOptions,
            plotOptions
        };
    },

    /**
     * For line or spline charts, highcharts can by default show a negative y-axis tick even when all the
     * charted measures are greater than zero. We get around this by setting the y-axis 'min' property
     * if there are no negative measures.
     */
    yAxisTickAdjust(yAxis :any, minMaxValues :any) :any {

        if (!yAxis) {
            // just a safe check
            return yAxis;
        }

        if (minMaxValues.min < 0) {
            // it means the min axis's value will have (and, should have) a negative value and there is no sense
            // to try to avoid it
            return yAxis;
        }

        // sometimes we have several y axis, so it's easy to work with array in all cases
        const yAxes = _.isArray(yAxis) ? yAxis : [yAxis];

        let lowestValue = minMaxValues.min;
        if (lowestValue >= 1) {
            // if it's possible let's decrease the lowest value
            lowestValue -= 1;
        }

        _.each(yAxes, (axis :any) => {
            if (axis.min) {
                // do NOT overwrite any pre-existing 'min' value
                return;
            }
            /* eslint-disable no-param-reassign */
            axis.min = lowestValue;
            axis.startOnTick = true;
            /* eslint-enable no-param-reassign */
        });

        return yAxis;
    }

}
